import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { 
  Calendar, Clock, Mail, Plus, Trash2, Play, Pause, 
  Send, Edit2, Check, X, Loader2, Facebook, Chrome, Music, BarChart3, Search
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_CONFIG = {
  all: { name: 'Todas las plataformas', color: '#d946ef', icon: BarChart3 },
  meta: { name: 'Meta Ads', color: '#0668E1', icon: Facebook },
  google: { name: 'Google Ads', color: '#EA4335', icon: Chrome },
  tiktok: { name: 'TikTok Ads', color: '#000000', icon: Music },
  ga4: { name: 'Google Analytics 4', color: '#F9AB00', icon: BarChart3 },
  gsc: { name: 'Search Console', color: '#4285F4', icon: Search }
};

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' }
];

export default function ScheduledReports() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const clientIdRef = useRef(clientId);
  useEffect(() => { clientIdRef.current = clientId; }, [clientId]);
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    platform: 'all',
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    recipient_emails: [''],
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/scheduled-reports${clientId ? `?client_id=${clientId}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setReports(reportsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      platform: 'all',
      frequency: 'weekly',
      day_of_week: 1,
      day_of_month: 1,
      recipient_emails: [''],
      is_active: true
    });
    setEditingReport(null);
  };

  const openModal = (report = null) => {
    if (report) {
      setEditingReport(report);
      setFormData({
        client_id: report.client_id || '',
        platform: report.platform || 'all',
        frequency: report.frequency,
        day_of_week: report.day_of_week || 1,
        day_of_month: report.day_of_month || 1,
        recipient_emails: report.recipient_emails || [''],
        is_active: report.is_active
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...formData.recipient_emails];
    newEmails[index] = value;
    setFormData({ ...formData, recipient_emails: newEmails });
  };

  const addEmail = () => {
    setFormData({ ...formData, recipient_emails: [...formData.recipient_emails, ''] });
  };

  const removeEmail = (index) => {
    const newEmails = formData.recipient_emails.filter((_, i) => i !== index);
    setFormData({ ...formData, recipient_emails: newEmails.length ? newEmails : [''] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validEmails = formData.recipient_emails.filter(email => email.trim());
    if (validEmails.length === 0) {
      toast.error('Agrega al menos un email destinatario');
      return;
    }

    const payload = {
      ...formData,
      client_id: formData.client_id || null,
      recipient_emails: validEmails
    };

    try {
      if (editingReport) {
        await axios.put(`${API}/scheduled-reports/${editingReport.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Reporte programado actualizado');
      } else {
        await axios.post(`${API}/scheduled-reports`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Reporte programado creado');
      }
      closeModal();
      fetchData();
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('Error al guardar el reporte');
    }
  };

  const toggleActive = async (report) => {
    try {
      await axios.put(`${API}/scheduled-reports/${report.id}`, {
        is_active: !report.is_active
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(report.is_active ? 'Reporte pausado' : 'Reporte activado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el reporte');
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('¿Eliminar este reporte programado?')) return;
    
    try {
      await axios.delete(`${API}/scheduled-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reporte eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el reporte');
    }
  };

  const sendNow = async (reportId) => {
    setSendingId(reportId);
    try {
      const response = await axios.post(`${API}/scheduled-reports/${reportId}/send-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el reporte');
    } finally {
      setSendingId(null);
    }
  };

  const getClientName = (clientId) => {
    if (!clientId) return 'Todos los clientes';
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Cliente desconocido';
  };

  const formatNextRun = (dateStr) => {
    if (!dateStr) return 'No programado';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ai-glow" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="scheduled-reports-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="font-heading font-bold text-4xl mb-2">Reportes Programados</h1>
            <p className="text-secondary">Configura envíos automáticos de informes por email</p>
          </div>
          
          <Button
            onClick={() => openModal()}
            className="bg-gradient-to-r from-ai-glow to-brand-meta text-white rounded-full h-12 px-6"
            data-testid="create-scheduled-report-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuevo Reporte
          </Button>
        </motion.div>

        {/* Reports List */}
        {reports.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-card p-12 text-center"
          >
            <Calendar className="w-16 h-16 mx-auto mb-4 text-secondary" />
            <h3 className="text-xl font-medium mb-2">No hay reportes programados</h3>
            <p className="text-secondary mb-6">Crea tu primer reporte automático para recibir informes por email</p>
            <Button onClick={() => openModal()} className="bg-ai-glow text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Crear Reporte
            </Button>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            {reports.map((report, index) => {
              const PlatformIcon = PLATFORM_CONFIG[report.platform]?.icon || BarChart3;
              return (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`glass-card p-6 ${!report.is_active ? 'opacity-60' : ''}`}
                  data-testid={`scheduled-report-${report.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div 
                        className="p-3 rounded-xl"
                        style={{ backgroundColor: `${PLATFORM_CONFIG[report.platform]?.color}20` }}
                      >
                        <PlatformIcon 
                          className="w-6 h-6" 
                          style={{ color: PLATFORM_CONFIG[report.platform]?.color }}
                        />
                      </div>
                      
                      <div>
                        <h3 className="font-medium text-lg">
                          {getClientName(report.client_id)} - {PLATFORM_CONFIG[report.platform]?.name}
                        </h3>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-secondary">
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {report.frequency === 'weekly' 
                              ? `Semanal - ${DAYS_OF_WEEK.find(d => d.value === report.day_of_week)?.label}`
                              : `Mensual - Día ${report.day_of_month}`
                            }
                          </span>
                          <span className="flex items-center">
                            <Mail className="w-4 h-4 mr-1" />
                            {report.recipient_emails?.length || 0} destinatario(s)
                          </span>
                        </div>
                        <p className="text-xs text-secondary mt-2">
                          Próximo envío: {formatNextRun(report.next_run_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => sendNow(report.id)}
                        disabled={sendingId === report.id}
                        className="hover:bg-success/20 hover:text-success"
                        title="Enviar ahora"
                      >
                        {sendingId === report.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleActive(report)}
                        className={report.is_active ? 'hover:bg-warning/20 hover:text-warning' : 'hover:bg-success/20 hover:text-success'}
                        title={report.is_active ? 'Pausar' : 'Activar'}
                      >
                        {report.is_active ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openModal(report)}
                        className="hover:bg-ai-glow/20 hover:text-ai-glow"
                        title="Editar"
                      >
                        <Edit2 className="w-5 h-5" />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteReport(report.id)}
                        className="hover:bg-error/20 hover:text-error"
                        title="Eliminar"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-xl">
                  {editingReport ? 'Editar Reporte' : 'Nuevo Reporte Programado'}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg text-gray-500 dark:text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Client */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">Cliente</label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white"
                  >
                    <option value="">Todos los clientes</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>

                {/* Platform */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">Plataforma</label>
                  <select
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white"
                  >
                    {Object.entries(PLATFORM_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.name}</option>
                    ))}
                  </select>
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">Frecuencia</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, frequency: 'weekly' })}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        formData.frequency === 'weekly'
                          ? 'border-ai-glow bg-ai-glow/20 text-ai-glow'
                          : 'border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-white/30'
                      }`}
                    >
                      Semanal
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, frequency: 'monthly' })}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        formData.frequency === 'monthly'
                          ? 'border-ai-glow bg-ai-glow/20 text-ai-glow'
                          : 'border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-white/30'
                      }`}
                    >
                      Mensual
                    </button>
                  </div>
                </div>

                {/* Day selection */}
                {formData.frequency === 'weekly' ? (
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">Día de la semana</label>
                    <select
                      value={formData.day_of_week}
                      onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                      className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white"
                    >
                      {DAYS_OF_WEEK.map(day => (
                        <option key={day.value} value={day.value}>{day.label}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">Día del mes</label>
                    <select
                      value={formData.day_of_month}
                      onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) })}
                      className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white"
                    >
                      {Array.from({ length: 28 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Recipient Emails */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">Emails destinatarios</label>
                  <div className="space-y-2">
                    {formData.recipient_emails.map((email, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          placeholder="email@ejemplo.com"
                          className="flex-1 bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder:text-gray-500"
                        />
                        {formData.recipient_emails.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEmail(index)}
                            className="p-2 hover:bg-error/20 rounded-lg text-error"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addEmail}
                      className="text-sm text-ai-glow hover:underline flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar email
                    </button>
                  </div>
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-sm">Reporte activo</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      formData.is_active ? 'bg-success' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={closeModal} className="rounded-full">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-ai-glow text-white rounded-full">
                    <Check className="w-4 h-4 mr-2" />
                    {editingReport ? 'Guardar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
